import { GridSortModel } from '@mui/x-data-grid-pro';
import { useQuery, UseQueryResult, useQueryClient, UseMutationResult, useMutation } from '@tanstack/react-query';

import { ServiceStatus, ServiceTargetResponse, ServiceType, ServiceTypeResponse } from '../@types/service.type';
import { getAllServiceTargets } from '../api/services';
import {
  createServiceType,
  getServiceTypeAvailableStatuses,
  getServiceTypeByCode,
  getServiceTypeByCodeForServiceUpdate,
  getServiceTypes,
  updateServiceType
} from '../api/serviceTypes';
import { ServiceTypeCodeKey, ServiceTypeKey, ServiceTypeTargetOptionKey } from '../configs/service-type';
import { AppServiceTypesKey } from '../configs/services';

export const useCreateServiceType = (code: string): UseMutationResult<ServiceTypeResponse, Error, ServiceTypeResponse> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ServiceTypeResponse) => {
      return await createServiceType(data);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [ServiceTypeKey]
      });
      await queryClient.invalidateQueries({
        queryKey: [ServiceTypeKey, code]
      });
    }
  });
};

export const useUpdateServiceType = (code: string): UseMutationResult<ServiceTypeResponse, Error, ServiceTypeResponse & { code: string }> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ServiceTypeResponse & { code: string }) => {
      return await updateServiceType(data.code, data);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [ServiceTypeKey]
      });
      await queryClient.invalidateQueries({
        queryKey: [ServiceTypeKey, code]
      });
    }
  });
};

export const useGetServiceTypesByCodeForServiceUpdate = (code: string): UseQueryResult<ServiceTypeResponse | undefined> => {
  return useQuery({
    queryKey: [ServiceTypeKey, code],
    queryFn: () => getServiceTypeByCodeForServiceUpdate(code),
    enabled: code !== '-1'
  });
};

export const useGetServiceTypesByCode = (code: string): UseQueryResult<ServiceTypeResponse> => {
  return useQuery({
    queryKey: [ServiceTypeKey, code],
    queryFn: () => getServiceTypeByCode(code)
  });
};

export const useGetServiceTypesByShortName = (code: string): UseQueryResult<ServiceTypeResponse> => {
  return useQuery({
    queryKey: [ServiceTypeCodeKey, code],
    queryFn: () => getServiceTypeByCode(code),
    retry: false,
    enabled: false
  });
};

export const useGetAllServiceTargets = (): UseQueryResult<ServiceTargetResponse[]> => {
  return useQuery({
    queryKey: [ServiceTypeTargetOptionKey],
    queryFn: () => getAllServiceTargets()
  });
};

export const useGetAllServiceTypes = (sortModel?: GridSortModel): UseQueryResult<ServiceTypeResponse[]> => {
  return useQuery({
    queryKey: [ServiceTypeKey],
    queryFn: () => getServiceTypes(sortModel)
  });
};

export const useGetServiceTypeAvailableStatuses = (): UseMutationResult<ServiceStatus[], Error, ServiceType[] | { serviceTypes: string[] }> => {
  return useMutation({
    mutationFn: async (data: ServiceType[] | { serviceTypes: string[] }) => {
      return await getServiceTypeAvailableStatuses(data);
    }
  });
};

export const useGetServiceTypes = (): UseQueryResult<ServiceTypeResponse[]> => {
  return useQuery({
    queryKey: [AppServiceTypesKey],
    queryFn: () => getServiceTypes(),
    staleTime: 2 * 60 * 60 * 1000
  });
};
